import { useI18n } from 'vue-i18n';
import type { IntegrationKey, IntegrationStatuses } from '@centric-os/types';
import { useSplitIoStore } from '@centric-os/stores/splitio/use-split-io';
import { INTEGRATION_DEFINITIONS, INTEGRATION_KEYS } from '../../../constants/integrations';

/**
 * Composable for working with all integrations at a station level
 * Filters the station's integrations by visibility, feature flags and integration status.
 * Results are integration keys in display order; look up UI details in INTEGRATION_DEFINITIONS.
 */
export default () => {
  const { t } = useI18n();
  const { isFeatureOn } = useSplitIoStore();

  /**
   * Whether an integration's feature flag (if it has one) is on
   * @param key - The integration key
   */
  const isIntegrationFeatureOn = (key: IntegrationKey): boolean => {
    const { featureFlag } = INTEGRATION_DEFINITIONS[key];
    return !featureFlag || isFeatureOn(featureFlag);
  };

  /**
   * Internal helper: Filters integrations by visibility and feature flags
   * Edge service can't check Harness feature flags, so we need to check them here
   * @param integrations - The integrations object from stationOverview
   * @returns Keys of visible integrations (regardless of integration status)
   */
  const getVisibleIntegrations = (
    integrations: IntegrationStatuses | undefined,
  ): IntegrationKey[] => {
    if (!integrations) return [];

    return INTEGRATION_KEYS.filter((key) => {
      if (!integrations[key]?.is_visible) {
        return false;
      }

      return isIntegrationFeatureOn(key);
    });
  };

  /**
   * Gets integrations that have been added to the station
   * @param integrations - The integrations object from stationOverview
   * @returns Keys of added integrations
   */
  const getAddedIntegrations = (integrations: IntegrationStatuses | undefined): IntegrationKey[] =>
    getVisibleIntegrations(integrations).filter(
      (key) => integrations?.[key]?.is_integrated === true,
    );

  /**
   * Gets integrations that can be added (not yet integrated) at the station
   * @param integrations - The integrations object from stationOverview
   * @returns Keys of available integrations
   */
  const getAvailableIntegrations = (
    integrations: IntegrationStatuses | undefined,
  ): IntegrationKey[] =>
    getVisibleIntegrations(integrations).filter(
      (key) => integrations?.[key]?.is_integrated === false,
    );

  /**
   * Name to show for a station integration: its displayName. Pass `{ withMarkers: true }` to add
   * markers such as a translated "(beta)" when the integration is flagged as beta.
   */
  const getIntegrationName = (key: IntegrationKey, { withMarkers = false } = {}): string => {
    const { displayName, beta } = INTEGRATION_DEFINITIONS[key];
    return withMarkers && beta
      ? t('sites.station.integrations.betaName', { name: displayName })
      : displayName;
  };

  return {
    getAddedIntegrations,
    getAvailableIntegrations,
    getIntegrationName,
    isIntegrationFeatureOn,
  };
};
